import { useState } from "react";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useCollection } from "@/hooks/useCollection";
import type { Action } from "@/lib/types";
import ImageUpload from "@/pages/admin/components/ImageUpload";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

const iconOptions = [
  "ri-heart-3-line",
  "ri-restaurant-line",
  "ri-hand-heart-line",
  "ri-group-line",
  "ri-user-heart-line",
  "ri-shield-check-line",
  "ri-flashlight-line",
  "ri-map-pin-line",
  "ri-team-line",
  "ri-book-open-line",
  "ri-first-aid-kit-line",
  "ri-eye-line",
  "ri-file-list-3-line",
  "ri-moon-clear-line",
  "ri-flag-line",
  "ri-chat-smile-3-line",
];

interface ActionForm {
  title: string;
  icon: string;
  description: string;
  stat_value: string;
  stat_label: string;
  gallery: string[];
  cagnotte_goal: string;
  cagnotte_collected: string;
}

const emptyForm: ActionForm = {
  title: "",
  icon: "ri-heart-3-line",
  description: "",
  stat_value: "",
  stat_label: "",
  gallery: [],
  cagnotte_goal: "",
  cagnotte_collected: "",
};

export default function AdminActions() {
  const { items, loading, error, reload } = useCollection<Action>("actions");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ActionForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [isError, setIsError] = useState(false);

  function set<K extends keyof ActionForm>(key: K, value: ActionForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function startAdd() {
    setEditingId(null);
    setForm({ ...emptyForm, gallery: [""] });
    setShowForm(true);
    setMsg("");
  }

  function startEdit(item: Action) {
    setEditingId(item.id);
    const gallery = item.gallery && item.gallery.length > 0 ? item.gallery : [""];
    setForm({
      title: item.title ?? "",
      icon: item.icon ?? "ri-heart-3-line",
      description: item.description ?? "",
      stat_value: item.stat_value ?? "",
      stat_label: item.stat_label ?? "",
      gallery,
      cagnotte_goal: item.cagnotte_goal != null ? String(item.cagnotte_goal) : "",
      cagnotte_collected:
        item.cagnotte_collected != null ? String(item.cagnotte_collected) : "",
    });
    setShowForm(true);
    setMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function updateGallery(index: number, value: string) {
    const next = [...form.gallery];
    next[index] = value;
    set("gallery", next);
  }

  function addGallery() {
    set("gallery", [...form.gallery, ""]);
  }

  function removeGallery(index: number) {
    const next = form.gallery.filter((_, i) => i !== index);
    set("gallery", next.length > 0 ? next : [""]);
  }

  function slugify(value: string): string {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-");
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setIsError(false);

    const gallery = form.gallery.filter((url) => url.trim() !== "");
    const slug = slugify(form.title);
    if (!slug) {
      setSaving(false);
      setMsg("Impossible de générer une URL lisible à partir du titre.");
      setIsError(true);
      return;
    }
    const cagnotte_goal = form.cagnotte_goal.trim()
      ? Number(form.cagnotte_goal)
      : null;
    const cagnotte_collected = form.cagnotte_collected.trim()
      ? Number(form.cagnotte_collected)
      : null;
    const payload = { ...form, slug, gallery, cagnotte_goal, cagnotte_collected };

    const res = editingId
      ? await supabase.from("actions").update(payload).eq("id", editingId)
      : await supabase
          .from("actions")
          .insert({ ...payload, sort_order: items.length });

    setSaving(false);
    if (res.error) {
      setMsg(res.error.message);
      setIsError(true);
      return;
    }

    setMsg(editingId ? "Action modifiée." : "Action ajoutée.");
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    reload();
  }

  async function handleDelete(id: string) {
    const { error: err } = await supabase.from("actions").delete().eq("id", id);
    if (err) {
      setMsg(err.message);
      setIsError(true);
      return;
    }
    reload();
  }

  async function move(id: string, dir: -1 | 1) {
    const idx = items.findIndex((i) => i.id === id);
    const targetIdx = idx + dir;
    if (idx < 0 || targetIdx < 0 || targetIdx >= items.length) return;

    const reordered = [...items];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);

    for (let i = 0; i < reordered.length; i += 1) {
      const { error: err } = await supabase
        .from("actions")
        .update({ sort_order: i })
        .eq("id", reordered[i].id);
      if (err) {
        setMsg(err.message);
        setIsError(true);
        return;
      }
    }
    reload();
  }

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Actions
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            Les domaines d'action affichés sur la page « Nos actions ».
          </p>
        </div>
        <button
          type="button"
          onClick={startAdd}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Ajouter une action
        </button>
      </div>

      {(msg || error) && (
        <div
          className={`mt-6 rounded-md px-4 py-3 text-sm ${
            isError ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"
          }`}
        >
          {isError ? msg : error || msg}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSave}
          className="mt-6 bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 space-y-5"
        >
          <h3 className="font-heading text-base font-semibold text-foreground-950">
            {editingId ? "Modifier l'action" : "Nouvelle action"}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Titre
              </label>
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Icône
              </label>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className={`${form.icon} text-lg`}></i>
                </span>
                <select
                  value={form.icon}
                  onChange={(e) => set("icon", e.target.value)}
                  className={inputClass}
                >
                  {iconOptions.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon.replace("ri-", "").replace("-line", "")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Description
              </label>
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Chiffre clé (valeur)
              </label>
              <input
                type="text"
                value={form.stat_value}
                onChange={(e) => set("stat_value", e.target.value)}
                placeholder="Ex : 86 400"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Chiffre clé (libellé)
              </label>
              <input
                type="text"
                value={form.stat_label}
                onChange={(e) => set("stat_label", e.target.value)}
                placeholder="Ex : repas distribués / an"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Objectif de la cagnotte (€)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.cagnotte_goal}
                onChange={(e) => set("cagnotte_goal", e.target.value)}
                placeholder="Ex : 50000"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Montant déjà collecté (€)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.cagnotte_collected}
                onChange={(e) => set("cagnotte_collected", e.target.value)}
                placeholder="Ex : 12000"
                className={inputClass}
              />
            </div>
            <p className="sm:col-span-2 text-xs text-foreground-500">
              Renseignez un objectif pour afficher la barre de progression et la
              possibilité de faire un don sur cette action.
            </p>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground-700">
                  Galerie photo (URLs)
                </label>
                <button
                  type="button"
                  onClick={addGallery}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  Ajouter une image
                </button>
              </div>
              <div className="space-y-3">
                {form.gallery.map((url, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex-1">
                      <ImageUpload
                        value={url}
                        onChange={(next) => updateGallery(index, next)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGallery(index)}
                      aria-label="Retirer cette image"
                      className="w-9 h-9 mt-1 shrink-0 flex items-center justify-center rounded-full text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement…
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 px-6 py-2.5 text-sm font-semibold transition cursor-pointer"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : (
        <div className="mt-8 bg-background-50 rounded-lg border border-background-200 divide-y divide-background-200">
          {items.length === 0 ? (
            <p className="p-8 text-sm text-foreground-500">
              Aucune action pour le moment. Cliquez sur « Ajouter une action ».
            </p>
          ) : (
            items.map((item, idx) => {
              const firstImage = item.gallery && item.gallery[0];
              return (
                <div key={item.id} className="flex items-center gap-4 p-4 md:p-5">
                  {firstImage ? (
                    <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden border border-background-200">
                      <img
                        src={firstImage}
                        alt={item.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-base font-bold text-foreground-950 truncate">
                      {item.title}
                    </p>
                    {item.stat_value ? (
                      <p className="text-sm text-foreground-600 truncate">
                        {item.stat_value} · {item.stat_label}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(item.id, -1)}
                      disabled={idx === 0}
                      aria-label="Monter"
                      className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-up-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => move(item.id, 1)}
                      disabled={idx === items.length - 1}
                      aria-label="Descendre"
                      className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-down-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => startEdit(item)}
                      aria-label="Modifier"
                      className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer"
                    >
                      <i className="ri-pencil-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      aria-label="Supprimer"
                      className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}